// app/AppThemeProvider.tsx
import { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { MantineProvider } from '@mantine/core';
import { buildTenantTheme } from '@/theme.ts';

export interface TenantBranding {
  colorCode: string;
  logoUrl?: string | null;
}

type BrandThemeContextValue = {
  setBranding: (branding: TenantBranding | null) => void;
  resetBranding: () => void;
};

const BrandThemeContext = createContext<BrandThemeContextValue | undefined>(undefined);

export const useBrandTheme = () => {
  const ctx = useContext(BrandThemeContext);
  if (!ctx) throw new Error('useBrandTheme must be used within <AppThemeProvider>');
  return ctx;
};

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<TenantBranding | null>(null);

  const theme = useMemo(() => buildTenantTheme(branding), [branding]);

  const ctxValue = useMemo(
    () => ({
      setBranding: (b: TenantBranding | null) => setBranding(b),
      resetBranding: () => setBranding(null),
    }),
    []
  );

  return (
    <BrandThemeContext.Provider value={ctxValue}>
      <MantineProvider theme={theme}>
        {children}
      </MantineProvider>
    </BrandThemeContext.Provider>
  );
}
